import { unwrap } from "@sagot/api-client";
import { ZOHO_INTEGRATION_KEY } from "@sagot/constants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { wsKeys } from "../shared";

/** Under the integrations key, so one invalidation of `wsKeys.integrations` refreshes the list and this status. */
export function zohoStatusKey(wsId: string) {
  return [...wsKeys.integrations(wsId), ZOHO_INTEGRATION_KEY, "status"] as const;
}

/** `GET /v1/integrations/zoho/status`: the connection state, the account that connected and the newest sync. */
export function useZohoStatus() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: zohoStatusKey(wsId), queryFn: () => unwrap(api.GET("/v1/integrations/zoho/status")) });
}

export function useZohoMutations() {
  const api = useApi();
  const wsId = useWorkspaceId();
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: wsKeys.integrations(wsId) });
  const connect = useMutation({
    mutationFn: (datacenter: string | undefined) => unwrap(api.GET("/v1/integrations/zoho/connect", { params: { query: datacenter ? { datacenter } : {} } })),
  });
  const disconnect = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/integrations/{key}", { params: { path: { key: ZOHO_INTEGRATION_KEY } } })),
    onSuccess: refresh,
  });
  return { connect, disconnect };
}

/** Coming back to a page restored from the back/forward cache (after Zoho's consent screen) clears the "Redirecting" state. */
export function useResetOnPageShow(reset: () => void): void {
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) reset();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => { window.removeEventListener("pageshow", onPageShow); };
  }, [reset]);
}
